import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ModalDeCambioDeEncargadoProps {
  abierta: boolean;
  /** Quien HOY tiene el informe en su app. */
  saliente: string | null;
  /** Quien lo va a tener. */
  entrante: string | null;
  onCancelar: () => void;
  onDecidir: (enviarAhora: boolean) => void;
}

/**
 * Advertencia antes de cambiar el encargado de un informe que ya está en la
 * app de terreno (decisión de Rodgers, 24-09-2026).
 *
 * Cambiarlo SACA el informe del teléfono del saliente, y el entrante no lo ve
 * hasta que se le envía la guía. Por eso la pregunta no es "¿estás seguro?"
 * sino qué hacer con la guía: enviarla ahora, al guardar, o dejarla para
 * después. En los dos casos el saliente recibe un correo que le explica que el
 * informe salió de su app, para que no lo tome por un error.
 *
 * El cambio recién se aplica al GUARDAR el formulario, igual que cualquier otro
 * campo: esta modal solo decide qué pasa en ese momento.
 */
export function ModalDeCambioDeEncargado({
  abierta,
  saliente,
  entrante,
  onCancelar,
  onDecidir,
}: ModalDeCambioDeEncargadoProps) {
  return (
    <Dialog
      open={abierta}
      onOpenChange={(estaAbierta) => {
        if (!estaAbierta) onCancelar();
      }}
    >
      <DialogContent className="sm:max-w-[32.5rem]">
        <DialogHeader>
          <DialogTitle>¿Cambiar el encargado de terreno?</DialogTitle>
          <DialogDescription>
            Este informe está en la app de terreno de{" "}
            <span className="text-foreground font-semibold">{saliente ?? "otro encargado"}</span>. Al
            guardar, saldrá de su app y le avisaremos por correo que la visita pasó a otra persona.
          </DialogDescription>
        </DialogHeader>

        <p className="text-ink-body text-sm">
          ¿Quieres enviarle la guía a{" "}
          <span className="text-ink font-semibold">{entrante ?? "el nuevo encargado"}</span> al guardar?
          Si la dejas para después, no verá el informe en su app hasta que la envíes desde "Guía de
          terreno".
        </p>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="outline" onClick={onCancelar}>
            Cancelar
          </Button>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button type="button" variant="outline" onClick={() => onDecidir(false)}>
              Cambiar y enviar después
            </Button>
            <Button type="button" onClick={() => onDecidir(true)}>
              Cambiar y enviar ahora
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
